import { ReactNode, MouseEvent, useState, useRef, KeyboardEvent, ChangeEvent, useEffect } from 'react';
import styles from './SelectNew.module.css';
import { classNames } from '../../utils';
import { Dropdown } from '../dropdown';
import CheckLineIcon from '../../atoms/icons/CheckLineIcon';
import ArrowDownSLineIcon from '../../atoms/icons/ArrowDownSLineIcon';

export interface Option {
    label?: ReactNode;
    value: string | null;
}

interface SelectNewProps {
    options: Option[];
    placeholder?: string;
    name?: string;
    multiple?: boolean;
    selected?: string | string[] | null;
    required?: boolean;
    hasInputField?: boolean;
    optionNotFoundText?: ReactNode;
    onChange?: (selectedOptions: Option[]) => void;
}

const SelectNew = ({
    options,
    placeholder,
    name,
    multiple = false,
    selected = undefined,
    required = false,
    hasInputField = false,
    optionNotFoundText,
    onChange,
}: SelectNewProps) => {
    const selectNewRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<HTMLSelectElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ isFocused, setIsFocused ] = useState<boolean>(false);
    const [ searchValue, setSearchValue] = useState<string>('');

    const handleButtonFocus = () => {
        setIsFocused(true);
    }

    const handleButtonBlur = () => {
        setTimeout(() => {
            if (selectNewRef.current && !selectNewRef.current.contains(document.activeElement as Node)) {
                if (hasInputField && inputRef.current) {
                    inputRef.current.value = '';
                    setSearchValue('');
                }

                setIsFocused(false);
            }
        }, 1);
    }

    useEffect(() => {
        if (inputRef.current) {
            if (isFocused) {
                inputRef.current.focus();
            } else {
                inputRef.current.blur();
            }
        }

        if (isFocused && selected?.length > 0) {
            // TODO: scroll to first selected item
        }
    }, [isFocused])

    const handleMouseDown = () => {
        setIsFocused((prev) => !prev)
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    }

    const handleOptionClick = (option: Option) => {
        if (multiple) {
            for (const selectOption of selectRef.current.options) {
                if (selectOption.value === option.value) {
                    selectOption.selected = !selectOption.selected;
                }
            }
        } else {
            selectRef.current.value = option.value;
        }

        // TODO: only dispatch change event if the value actually changed
        const event = new Event('change', { bubbles: true });
        selectRef.current.dispatchEvent(event);

        if (!multiple) {
            setIsFocused(false);
        }
    }

    const isSelected = (option: Option): boolean => {
        if (multiple && Array.isArray(selected)) {
            return selected.includes(option.value);
        } else {
            return option.value === selected;
        }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                setIsFocused(false);
                buttonRef.current.blur();
                break;
            case 'ArrowDown':
                event.preventDefault();
                // TODO: implement arrow down highlighting
                break;
            case 'ArrowUp':
                event.preventDefault();
                // TODO: implement arrow up highlighting
                break;
            case 'Enter':
                event.preventDefault();
                // TODO: implement highlight selection
                break;
            default:
                break;
        }
    }

    const onSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (onChange === undefined) {
            return;
        }

        if (multiple) {
            const selectedOptions = Array.from(e.target.options)
                .filter(option => option.selected)
                .map(option => options.find(o => o.value === option.value))
                .filter((option): option is Option => option !== undefined);

            onChange(selectedOptions);
        } else {
            const option = options.find(option => {
                return Array.from(e.target.options)
                    .filter(option => option.selected)
                    .map(option => option.value)
                    .includes(option.value);
            });
            if (option !== undefined) {
                onChange([option]);
            } else {
                onChange([]);
            }
        }
    }

    const renderSelectedOption = (selectedValue: string | null): ReactNode => {
        const option = options.find(option => option.value === selectedValue);

        if (!option) {
            return null;
        }

        if (option.label !== undefined) {
            return (
                <span>
                    {option.label}
                </span>
            );
        }

        return option.value;
    }

    const renderSelected = () => {
        if (selected === null || selected === undefined || selected === '') {
            if (placeholder) {
                return <div className={styles.placeholder}>{placeholder}</div>;
            } else {
                return <>&nbsp;</>;
            }
        }

        if (multiple && Array.isArray(selected)) {
            return (
                <span>
                    {selected.map(renderSelectedOption)}
                </span>
            );
        } else {
            return renderSelectedOption(selected as string|null);
        }
    }

    const renderSelect = () => {
        if (hasInputField) {
            return (
                <div
                    className={classNames(styles.button, isFocused && styles.focused)}
                    onMouseDown={handleMouseDown}
                >
                    {selected?.length === 0 || isFocused ? (
                        <input
                            ref={inputRef}
                            onFocus={handleButtonFocus}
                            onBlur={handleButtonBlur}
                            onChange={handleInputChange}
                            value={searchValue}
                            placeholder={placeholder}
                        />
                    ) : (
                        <>
                            {renderSelected()}
                        </>
                    )}
                    <ArrowDownSLineIcon className={classNames(styles.arrowDownIcon, !isFocused && styles.isOpen)}/>
                </div>
            );
        }

        return (
            <button
                ref={buttonRef}
                className={classNames(styles.button, isFocused && styles.focused)}
                type="button"
                onFocus={handleButtonFocus}
                onBlur={handleButtonBlur}
                onMouseDown={handleMouseDown}
            >
                {renderSelected()}
                <ArrowDownSLineIcon className={classNames(styles.arrowDownIcon, !isFocused && styles.isOpen)}/>
            </button>
        )
    }

    const renderOption = (option: Option) => {
        const optionValue = ((typeof option.label === 'string') ? option.label : option.value);

        if (optionValue === null) {
            return null;
        }

        if (
            searchValue !== ''
            && !optionValue.trim().toLowerCase().includes(searchValue.trim().toLowerCase())
        ) {
            return null;
        }

        return (
            <li
                key={option.value}
                className={classNames(styles.option, isSelected(option) && styles.selected)}
                onClick={() => handleOptionClick(option)}
            >{option.label} <CheckLineIcon className={styles.checkIcon}/></li>
        );
    }

    const renderOptions = () => {
        const renderedOptions = options.map(renderOption);

        if (renderedOptions.find(item => item !== null) === undefined) {
            return (
                <li className={styles.option}>
                    {optionNotFoundText}
                </li>
            );
        }

        return (
            <>
                {renderedOptions}
            </>
        )
    }

    return (
        <>
            <div
                ref={selectNewRef}
                className={styles.selectNew}
                onKeyDown={handleKeyDown}
            >
                <select
                    ref={selectRef}
                    placeholder={placeholder}
                    name={name}
                    multiple={multiple}
                    value={selected === null ? undefined : selected}
                    onChange={onSelectChange}
                    required={required}
                    hidden
                >
                    <option value=""></option>
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            selected={isSelected(option)}
                        >{option.value}</option>
                    ))}
                </select>
                {renderSelect()}
                <Dropdown
                    isActive={isFocused}
                    fullWidth
                >
                    <ul
                        className={styles.options}
                        onMouseDown={(e: MouseEvent) => e.preventDefault()}
                    >
                        {renderOptions()}
                    </ul>
                </Dropdown>
            </div>
        </>
    );
}

export default SelectNew;
